/**
 * VersonaChatterModule — overheard conversations (eavesdrop bubbles, 2026-09-10).
 *
 * Two versonas standing together in the `chatting` / `social` state get a
 * thread of spoken turns (lib/versona_chatter, pure + deterministic) shown as
 * a speech bubble over whoever is speaking — but ONLY to a player within
 * earshot (CHATTER_RANGE_M), fading in from the edge of it, so listening in
 * means walking up. The server's own staged street scenes (a real encounter:
 * greeting, grievance, rumor, small talk — LivingEventsModule's StreetScene)
 * take precedence: a pair with a live scene is left to it.
 *
 * Rendering follows the living-events laws: canvas plates are unique
 * materials (allowed), redrawn IN PLACE per turn (never a texture per turn),
 * torn down through disposeLater, absolute coordinates under the scene
 * (the FP64 rebase law).
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import type { App } from 'enginex/core/App.js';
import type { IModule } from 'enginex/core/App.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { atlas } from '../state.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { drawTextPlate } from './living-events/fx_textures.js';
import { wrap } from './living-events/stages/StreetScene.js';
import type { EnvironmentModule } from './EnvironmentModule.js';
import { versonaTts } from '../lib/tts/versona_tts.js';
import { voiceFor } from '../lib/tts/versona_voice.js';
import {
  selectPairs, chatterLine, chatterOpacity, CHATTER_TURN_S, CHATTER_RANGE_M, type ChatterPair, type ChatterFacts,
} from '../lib/versona_chatter.js';

interface Thread {
  pair: ChatterPair;
  turn: number;
  nextAt: number;
  speaker: 0 | 1;
  text: string;
  shownAt: number;
  sprite: THREE.Sprite;
  canvas: HTMLCanvasElement;
  tex: THREE.CanvasTexture;
}

const PLAN_EVERY_S = 0.5;
const FADE_IN_S = 0.35;

export class VersonaChatterModule implements IModule {
  readonly name = 'VersonaChatter';
  private readonly root = new THREE.Group();
  private readonly threads = new Map<string, Thread>();
  private planAcc = 0;
  private t = 0;
  private sceneIds = new Set<string>();
  private pairs: ChatterPair[] = [];
  private app: App | null = null;

  constructor(private readonly env: EnvironmentModule) {}

  init(app: App): void {
    this.app = app;
    this.root.name = 'versona-chatter';
    app.world.scene.add(this.root);
    app.world.addSystem(makeSystem('VersonaChatter', Phase.Animation, 41, (dt) => this.frame(dt)));
    (window as unknown as Record<string, unknown>).__chatter = () => ({
      pairs: this.pairs.map((p) => ({ key: p.key, dist: Math.round(p.dist * 10) / 10 })),
      threads: [...this.threads.values()].map((th) => ({ key: th.pair.key, turn: th.turn, speaker: th.speaker, text: th.text, opacity: (th.sprite.material as THREE.SpriteMaterial).opacity })),
      voices: { ...this.voiceStats, refused: { ...this.voiceStats.refused } },
      sceneIds: this.sceneIds.size,
    });
  }

  private facts(): ChatterFacts {
    const weather = this.app?.getModule('Weather') as { getPrecip?(): { kind: string } } | undefined;
    const sky = this.app?.getModule('Sky') as { timeOfDay?: number } | undefined;
    const precip = weather?.getPrecip?.().kind ?? 'none';
    const tod = typeof sky?.timeOfDay === 'number' ? sky.timeOfDay : 0.5;
    return { precip: precip === 'rain' || precip === 'snow' ? precip : 'none', tod };
  }

  /** Versonas a live street scene already speaks for (host + participants). */
  private refreshSceneIds(): void {
    this.sceneIds.clear();
    const db = atlas.conn?.db as unknown as { livingEvent?: { iter(): Iterable<{ family: string; phase: string; hostId: bigint; participantsJson: string }> } } | undefined;
    if (!db?.livingEvent) return;
    try {
      for (const e of db.livingEvent.iter()) {
        if (e.family !== 'street' || e.phase !== 'live') continue;
        this.sceneIds.add(e.hostId.toString());
        try { for (const id of JSON.parse(e.participantsJson) as string[]) this.sceneIds.add(id); } catch { /* malformed row */ }
      }
    } catch { /* not subscribed */ }
  }

  private plan(): void {
    this.refreshSceneIds();
    const px = atlas.localX, pz = atlas.localZ;
    const slots = this.env.chattingSlots(px, pz, CHATTER_RANGE_M);
    this.pairs = selectPairs(slots, px, pz).filter((p) => !this.sceneIds.has(p.a) && !this.sceneIds.has(p.b));
    const keep = new Set(this.pairs.map((p) => p.key));
    for (const [key, th] of this.threads) {
      if (keep.has(key)) continue;
      this.root.remove(th.sprite);
      disposeLater(th.sprite.material);
      disposeLater(th.tex);
      this.threads.delete(key);
    }
    for (const p of this.pairs) {
      const th = this.threads.get(p.key);
      if (th) { th.pair = p; continue; }
      this.threads.set(p.key, this.open(p));
    }
  }

  private open(pair: ChatterPair): Thread {
    const canvas = document.createElement('canvas');
    canvas.width = 512; canvas.height = 148;
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(markMrtOverlay(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, opacity: 0 })));
    sprite.scale.set(3.6, (canvas.height / 512) * 3.6, 1);
    sprite.renderOrder = 5;
    this.root.add(sprite);
    // A thread resumes where it was — its turn index is the clock, so a
    // conversation the player walks away from and back to has moved on.
    const turn = Math.floor((this.t / CHATTER_TURN_S) + hashTurn(pair.key)) % 1_000_000;
    const th: Thread = { pair, turn, nextAt: 0, speaker: 0, text: '', shownAt: this.t, sprite, canvas, tex };
    this.speak(th);
    return th;
  }

  private speak(th: Thread): void {
    const line = chatterLine(th.pair.key, th.turn, this.facts());
    th.speaker = line.speaker;
    th.text = line.text;
    th.shownAt = this.t;
    th.nextAt = this.t + CHATTER_TURN_S;
    const lines = wrap(line.text, 30).slice(0, 3);
    th.canvas.height = 60 + lines.length * 44;
    drawTextPlate(th.canvas, lines.map((t) => ({ text: t, font: '600 28px system-ui', color: '#1c1430' })), 'rgba(244,240,255,0.92)', 'rgba(60,40,90,0.5)');
    th.tex.needsUpdate = true;
    th.sprite.scale.set(3.6, (th.canvas.height / 512) * 3.6, 1);
    this.voice(th, line.speaker === 0 ? th.pair.a : th.pair.b, line.text);
  }

  /** AMBIENT VOICES (2026-09-15, the speech budget): an overheard turn is spoken in the
   *  speaker's own voice only into silence, within earshot, when the line is short —
   *  never over the conversation you opened. Refusals are counted, not queued. */
  private readonly voiceStats = { spoken: 0, refused: { busy: 0, gap: 0, far: 0, long: 0 }, lastSpeaker: '' };
  private voice(th: Thread, who: string, text: string): void {
    const tts = versonaTts();
    if (!tts.ambientEnabled()) return;
    const p = this.env.versonaPosition(who);
    if (!p) return;
    const dist = Math.hypot(p.x - atlas.localX, p.z - atlas.localZ);
    const v = tts.ambientVerdict(dist, text.length);
    if (v !== 'ok') { this.voiceStats.refused[v]++; return; }
    let id: bigint;
    try { id = BigInt(who); } catch { return; }
    const u = tts.speak(who, text, voiceFor(this.env.bodyKeyOf(who), id), 'ambient');
    if (u) { this.voiceStats.spoken++; this.voiceStats.lastSpeaker = who; }
    void th;
  }

  private frame(dt: number): void {
    this.t += dt;
    this.planAcc += dt;
    if (this.planAcc >= PLAN_EVERY_S) { this.planAcc = 0; this.plan(); }
    if (this.threads.size === 0) return;
    const px = atlas.localX, pz = atlas.localZ;
    for (const th of this.threads.values()) {
      if (this.t >= th.nextAt) { th.turn++; this.speak(th); }
      const who = th.speaker === 0 ? th.pair.a : th.pair.b;
      const p = this.env.versonaPosition(who) ?? this.env.versonaPosition(th.speaker === 0 ? th.pair.b : th.pair.a);
      const mat = th.sprite.material as THREE.SpriteMaterial;
      if (!p) { mat.opacity = 0; continue; }
      th.sprite.position.set(p.x, p.y + 2.3 + Math.sin(this.t * 1.4) * 0.04, p.z);
      const dist = Math.hypot(p.x - px, p.z - pz);
      const fadeIn = Math.min(1, (this.t - th.shownAt) / FADE_IN_S);
      mat.opacity = chatterOpacity(dist) * fadeIn;
    }
  }

  dispose(): void {
    for (const th of this.threads.values()) { this.root.remove(th.sprite); disposeLater(th.sprite.material); disposeLater(th.tex); }
    this.threads.clear();
    this.root.parent?.remove(this.root);
  }
}

function hashTurn(key: string): number {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return h % 97;
}
